import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import type { TEntityId, ISelectItem } from '~/types/models'
import type {
  ICompanyMasterService,
  ICompanyMSService,
  ICompany,
} from '~/types/company/models'

const apiEntityPath = '/api/companymasterservices'

/**
 * One service group, with its companyMSServices nested.
 *
 * The nesting is the point: the server's CompanyMasterService entity embeds
 * `company_m_s_services`, so the msServices of a group cost one GET rather than
 * a second round trip.
 *
 * ⚠ There is NO `/companymasterservices/{id}/companymsservices` on the real
 * API. The mock has one -- and nothing has ever called it -- so a service
 * written against it passes every local suite and 404s in production. See the
 * note on the mock handler.
 */
export async function getCompanyMasterService(id: TEntityId) {
  const callingMn = getCompanyMasterService.name
  const apiPath = `${apiEntityPath}/${id}`
  return httpGet<ICompanyMasterService>({ callingMn, apiPath })
}

export async function getCompanyFromCompanyMasterService(id: TEntityId) {
  const callingMn = getCompanyFromCompanyMasterService.name
  const apiPath = `${apiEntityPath}/${id}/company`
  return httpGet<ICompany>({ callingMn, apiPath })
}

export async function createCompanyMasterService(
  entity: ICompanyMasterService
) {
  const callingMn = createCompanyMasterService.name
  const apiPath = apiEntityPath
  return httpPost<ICompanyMasterService>({
    callingMn,
    apiPath,
    entity,
  })
}

export async function getSuppliersFromCompanyMasterService(id: TEntityId) {
  const callingMn = getSuppliersFromCompanyMasterService.name
  const apiPath = `${apiEntityPath}/${id}/suppliers`
  return httpGet<ICompanyMasterService>({ callingMn, apiPath })
}

export async function updateCompanyMasterService(
  entityData: Partial<ICompanyMasterService>,
  id: TEntityId
) {
  const callingMn = updateCompanyMasterService.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialICompanyMasterService = Partial<ICompanyMasterService>
  return httpPatchMerge<TPartialICompanyMasterService>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteCompanyMasterService(
  entityData: ICompanyMasterService
) {
  const callingMn = deleteCompanyMasterService.name
  const apiPath = apiEntityPath
  return httpDelete<ICompanyMasterService>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function getPotentialLocationSelectItemsForServicesOfCompanyMasterService(
  id: TEntityId
) {
  const callingMn =
    getPotentialLocationSelectItemsForServicesOfCompanyMasterService.name
  const apiPath = `${apiEntityPath}/${id}/potentiallocations`
  return httpGet<ISelectItem[]>({
    callingMn,
    apiPath,
  })
}
